import gql from 'graphql-tag';

const OBJECT_DEFINITION_FRAGMENT = gql`
  fragment ObjectDefinitionFields on ObjectDefinition {
    id
    organisationId
    typeId
    name
    description
    categoryId
    tags
    status
    version
    activeVersion
    hasUnpublishedChanges
    draftSpec
    publishedSpec
    publishedAt
    assets {
      id
      kind
      originalName
      size
      url
      createdAt
    }
    basedOnBuiltinId
    templateId
    smart
    createdAt
    updatedAt
  }
`;

const OBJECT_LIBRARY_FRAGMENT = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  fragment ObjectLibraryFields on ObjectLibrary {
    organisationId
    canEdit
    config {
      hiddenTypeIds
      categories {
        id
        label
        order
      }
      typeOrder {
        typeId
        categoryId
        order
      }
    }
    definitions {
      ...ObjectDefinitionFields
    }
  }
`;

export const PROJECT_OBJECT_LIBRARY = gql`
  ${OBJECT_LIBRARY_FRAGMENT}
  query projectObjectLibrary($projectId: ID!) {
    projectObjectLibrary(projectId: $projectId) {
      ...ObjectLibraryFields
    }
  }
`;

export const ORGANISATION_OBJECT_LIBRARY = gql`
  ${OBJECT_LIBRARY_FRAGMENT}
  query organisationObjectLibrary($organisationId: ID!, $includeArchived: Boolean) {
    organisationObjectLibrary(organisationId: $organisationId, includeArchived: $includeArchived) {
      ...ObjectLibraryFields
    }
  }
`;

export const OBJECT_DEFINITION = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  query objectDefinition($organisationId: ID!, $definitionId: ID!) {
    objectDefinition(organisationId: $organisationId, definitionId: $definitionId) {
      ...ObjectDefinitionFields
    }
  }
`;

export const ORGANISATION_OBJECT_LIBRARY_UPLOAD_SAS = gql`
  query organisationObjectLibraryUploadSAS($organisationId: ID!) {
    organisationObjectLibraryUploadSAS(organisationId: $organisationId)
  }
`;

export const OBJECT_DEFINITION_CREATE = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionCreate($organisationId: ID!, $name: String!, $categoryId: String!, $spec: JSON!, $templateId: String, $basedOnBuiltinId: String, $duplicateOfDefinitionId: ID, $smart: Boolean) {
    objectDefinitionCreate(
      organisationId: $organisationId
      name: $name
      categoryId: $categoryId
      spec: $spec
      templateId: $templateId
      basedOnBuiltinId: $basedOnBuiltinId
      duplicateOfDefinitionId: $duplicateOfDefinitionId
      smart: $smart
    ) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_UPDATE_DETAILS = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionUpdateDetails($organisationId: ID!, $definitionId: ID!, $name: String!, $description: String, $categoryId: String!, $tags: [String!]!) {
    objectDefinitionUpdateDetails(organisationId: $organisationId, definitionId: $definitionId, name: $name, description: $description, categoryId: $categoryId, tags: $tags) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_SAVE_DRAFT = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionSaveDraft($organisationId: ID!, $definitionId: ID!, $spec: JSON!) {
    objectDefinitionSaveDraft(organisationId: $organisationId, definitionId: $definitionId, spec: $spec) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_PUBLISH = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionPublish($organisationId: ID!, $definitionId: ID!) {
    objectDefinitionPublish(organisationId: $organisationId, definitionId: $definitionId) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_DISCARD_DRAFT = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionDiscardDraft($organisationId: ID!, $definitionId: ID!) {
    objectDefinitionDiscardDraft(organisationId: $organisationId, definitionId: $definitionId) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_SET_ARCHIVED = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionSetArchived($organisationId: ID!, $definitionId: ID!, $archived: Boolean!) {
    objectDefinitionSetArchived(organisationId: $organisationId, definitionId: $definitionId, archived: $archived) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_DELETE = gql`
  mutation objectDefinitionDelete($organisationId: ID!, $definitionId: ID!) {
    objectDefinitionDelete(organisationId: $organisationId, definitionId: $definitionId)
  }
`;

export const OBJECT_DEFINITION_ATTACH_ASSET = gql`
  mutation objectDefinitionAttachAsset($organisationId: ID!, $definitionId: ID!, $kind: ObjectDefinitionAssetKind!, $blobName: String!, $originalName: String!) {
    objectDefinitionAttachAsset(organisationId: $organisationId, definitionId: $definitionId, kind: $kind, blobName: $blobName, originalName: $originalName) {
      id
      kind
      originalName
      size
      url
      createdAt
    }
  }
`;

export const ORGANISATION_OBJECT_LIBRARY_UPDATE_CONFIG = gql`
  mutation organisationObjectLibraryUpdateConfig($organisationId: ID!, $config: ObjectLibraryConfigInput!) {
    organisationObjectLibraryUpdateConfig(organisationId: $organisationId, config: $config) {
      hiddenTypeIds
      categories {
        id
        label
        order
      }
      typeOrder {
        typeId
        categoryId
        order
      }
    }
  }
`;

export const OBJECT_DEFINITION_VERSIONS = gql`
  query objectDefinitionVersions($organisationId: ID!, $definitionId: ID!) {
    objectDefinitionVersions(organisationId: $organisationId, definitionId: $definitionId) {
      id
      version
      spec
      name
      categoryId
      publishedAt
      publishedByName
      isCurrent
    }
  }
`;

export const OBJECT_DEFINITION_RESTORE_VERSION = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionRestoreVersion($organisationId: ID!, $definitionId: ID!, $version: Int!) {
    objectDefinitionRestoreVersion(organisationId: $organisationId, definitionId: $definitionId, version: $version) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_ACTIVATE_VERSION = gql`
  ${OBJECT_DEFINITION_FRAGMENT}
  mutation objectDefinitionActivateVersion($organisationId: ID!, $definitionId: ID!, $version: Int!) {
    objectDefinitionActivateVersion(organisationId: $organisationId, definitionId: $definitionId, version: $version) {
      ...ObjectDefinitionFields
    }
  }
`;

export const OBJECT_DEFINITION_DELETE_VERSION = gql`
  mutation objectDefinitionDeleteVersion($organisationId: ID!, $definitionId: ID!, $version: Int!) {
    objectDefinitionDeleteVersion(organisationId: $organisationId, definitionId: $definitionId, version: $version)
  }
`;

export const OBJECT_DEFINITION_GENERATE = gql`
  mutation objectDefinitionGenerate($organisationId: ID!, $prompt: String!, $spec: JSON, $kind: ObjectDefinitionKind, $image: String, $progressChannel: String) {
    objectDefinitionGenerate(organisationId: $organisationId, prompt: $prompt, spec: $spec, kind: $kind, image: $image, progressChannel: $progressChannel) {
      spec
      summary
      name
    }
  }
`;
